import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Hash, FileText, Calendar, MapPin, User, Building } from 'lucide-react'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import type { Project } from '../data/projects'
import { formatCurrency } from '../lib/orders-utils'
import { formatDisplayDate } from '../lib/date-utils'
import { useThemeTokens } from '../themes'
import { createApiClient, getToken } from '../lib/api'
import { getProjectById } from '../data/projects'
import type { OrderResponse } from '../contracts/index.js'

export const ViewOrder: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const theme = useThemeTokens()
  const [order, setOrder] = useState<OrderResponse | null>(null)
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (orderId) {
      loadOrder(orderId)
    }
  }, [orderId])

  const loadOrder = async (id: string) => {
    try {
      setLoading(true)
      const apiClient = await createApiClient()
      const token = getToken() || 'dev-token-1'
      
      // Get all orders and find the specific one - use contract types directly
      const allOrders = await apiClient.getOrdersWithFilters(token)
      const orderData = allOrders.find(order => order.id === parseInt(id))
      
      if (!orderData) {
        throw new Error('Order not found')
      }
      
      // Use OrderResponse directly - no transformation needed
      setOrder(orderData)
      
      // Load project data if project_id exists
      if (orderData.project_id) {
        const projectData = getProjectById(orderData.project_id)
        setProject(projectData || null)
      }
    } catch (err) {
      setError('Failed to load order details')
      console.error('Error loading order:', err)
    } finally {
      setLoading(false)
    }
  }

  const calculateDaysOld = (createdDate: string): number => {
    const orderDate = new Date(createdDate)
    const today = new Date()
    const diffTime = Math.abs(today.getTime() - orderDate.getTime())
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  const getStatusVariant = (status: OrderResponse['status']) => {
    switch (status) {
      case 'pending': return 'draft'
      case 'active': return 'submitted'
      case 'completed': return 'completed'
      case 'cancelled': return 'draft'
      default: return 'default'
    }
  }

  const handleEditOrder = () => {
    navigate(`/orders/edit/${orderId}`)
  }

  const handleBackToOrders = () => {
    navigate('/orders')
  }

  // Helper function is no longer needed - unit comes from contract

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner">Loading order details...</div>
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        <p>Error: {error || 'Order not found'}</p>
        <Button onClick={handleBackToOrders} className="mt-2">
          ← Back to Orders
        </Button>
      </div>
    )
  }

  const daysOld = calculateDaysOld(order.created_at)

  // Styles
  const pageStyles: React.CSSProperties = {
    minHeight: '100vh',
    padding: theme.spacing.xl,
    fontFamily: theme.typography.fontFamily,
    color: theme.colors.text,
    backgroundColor: theme.colors.background
  }

  const headerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xl
  }

  const cardStyles: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
    padding: theme.spacing.lg,
    boxShadow: theme.shadows.sm,
    marginBottom: theme.spacing.lg
  }

  const sectionHeaderStyles: React.CSSProperties = {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
    borderBottom: `2px solid ${theme.colors.border}`,
    paddingBottom: theme.spacing.sm
  }

  const infoRowStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
    fontSize: theme.typography.fontSize.sm
  }

  const labelStyles: React.CSSProperties = {
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.colors.textSecondary,
    minWidth: '120px'
  }

  const valueStyles: React.CSSProperties = {
    color: theme.colors.text
  }

  return (
    <div style={pageStyles}>
      {/* Header */}
      <div style={headerStyles}>
        <div>
          <h1 style={{ 
            fontSize: theme.typography.fontSize.xl, 
            fontWeight: theme.typography.fontWeight.bold,
            margin: 0,
            marginBottom: theme.spacing.xs
          }}>
            Order #{order.id}
          </h1>
          <p style={{ margin: 0, color: theme.colors.textSecondary }}>
            Created {formatDisplayDate(order.created_at)} • {daysOld} days old
          </p>
        </div>
        <div style={{ display: 'flex', gap: theme.spacing.md }}>
          <Button onClick={handleEditOrder} variant="secondary">
            Edit Order
          </Button>
          <Button onClick={handleBackToOrders} variant="ghost">
            ← Back to Orders
          </Button>
        </div>
      </div>

      {/* Order Status */}
      <div style={cardStyles}>
        <div style={sectionHeaderStyles}>Order Status</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
          <Badge variant={getStatusVariant(order.status)}>
            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
          </Badge>
          <span style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold }}>
            {formatCurrency(order.total_cost || order.amount)}
          </span>
        </div>
      </div>

      {/* Order Details */}
      <div style={cardStyles}>
        <div style={sectionHeaderStyles}>Order Details</div>
        
        <div style={infoRowStyles}>
          <FileText size={16} color={theme.colors.textSecondary} />
          <span style={labelStyles}>Order Title:</span>
          <span style={valueStyles}>{order.title}</span>
        </div>

        <div style={infoRowStyles}>
          <Hash size={16} color={theme.colors.textSecondary} />
          <span style={labelStyles}>Job Number:</span>
          <span style={valueStyles}>{order.job_number || 'Not assigned'}</span>
        </div>

        <div style={infoRowStyles}>
          <Calendar size={16} color={theme.colors.textSecondary} />
          <span style={labelStyles}>Delivery Date:</span>
          <span style={valueStyles}>
            {order.delivery_date ? formatDisplayDate(order.delivery_date) : 'Not specified'}
          </span>
        </div>

        {order.notes && (
          <div style={infoRowStyles}>
            <FileText size={16} color={theme.colors.textSecondary} />
            <span style={labelStyles}>Notes:</span>
            <span style={valueStyles}>{order.notes}</span>
          </div>
        )}

        {project && (
          <>
            <div style={infoRowStyles}>
              <Building size={16} color={theme.colors.textSecondary} />
              <span style={labelStyles}>Project:</span>
              <span style={valueStyles}>{project.name}</span>
            </div>
            
            <div style={infoRowStyles}>
              <MapPin size={16} color={theme.colors.textSecondary} />
              <span style={labelStyles}>Location:</span>
              <span style={valueStyles}>{project.location}</span>
            </div>
          </>
        )}

        <div style={infoRowStyles}>
          <User size={16} color={theme.colors.textSecondary} />
          <span style={labelStyles}>Created By:</span>
          <span style={valueStyles}>User #{order.user_id}</span>
        </div>
      </div>

      {/* Order Items */}
      {order.items && order.items.length > 0 && (
        <div style={cardStyles}>
          <div style={sectionHeaderStyles}>Order Items</div>
          
          {order.items.map((item) => (
            <div key={item.id} style={{
              padding: theme.spacing.md,
              borderBottom: `1px solid ${theme.colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontWeight: theme.typography.fontWeight.medium }}>
                  {item.product_name}
                </div>
                <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                  ID: {item.product_id} • Qty: {item.quantity}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: theme.typography.fontWeight.bold }}>
                  {formatCurrency(item.total_price)}
                </div>
                <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                  {formatCurrency(item.unit_price)} {item.unit}
                </div>
              </div>
            </div>
          ))}
          
          <div style={{
            marginTop: theme.spacing.lg,
            paddingTop: theme.spacing.md,
            borderTop: `1px solid ${theme.colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ 
              fontSize: theme.typography.fontSize.lg, 
              fontWeight: theme.typography.fontWeight.bold 
            }}>
              Total: {formatCurrency(order.total_cost || order.amount)}
            </span>
          </div>
        </div>
      )}

      {/* Additional Actions */}
      <div style={{ textAlign: 'center', marginTop: theme.spacing.xl }}>
        <Button onClick={handleBackToOrders} variant="secondary">
          ← Back to Orders
        </Button>
      </div>
    </div>
  )
} 